import { useHandler } from "@redotech/react-util/hook";
import {
  rcoProductTagsSegmentSchema,
  RcoProductTagsSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import type { ProductTagsSegmentListMultipleItemMembershipOperator } from "@redotech/redo-model/checkouts/checkout-tree";
import {
  ProductTagsSegmentListMultipleItemMembershipOperator as ProductTagsSegmentListMultipleItemMembershipOperatorEnum,
  SegmentListMultipleItemMembershipOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { ProductTagsSelector } from "../../common/product-tags-selector";

export const ProductTagsInspectorContent = memo(
  function ProductTagsInspectorContent({ step }: { step: RcoSegmentStep }) {
    const segment = useMemo(
      () => rcoProductTagsSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.cartOperator, segment.operator)}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.tags.map((tag, index) => (
            <RedoBadge color="gray" key={index} size="sm" text={tag} />
          ))}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  cartOperator: RcoProductTagsSegmentSchema["cartOperator"],
  operator: RcoProductTagsSegmentSchema["operator"],
) => {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "Any product in the cart has at least one tag in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "Any product in the cart does not have any tag in the list:";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "All products in the cart have at least one tag in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "All products in the cart do not have any tag in the list";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "No product in the cart has at least one tag in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "No product in the cart does not have any tag in the list:";
        default:
          assertNever(operator);
      }
      break;
    default:
      assertNever(cartOperator);
  }
};

export const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "Any",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

export const productTagsSegmentListMultipleItemMembershipOperatorToText: Record<
  ProductTagsSegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "any",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "no",
};

export const EditableProductTagsInspectorContent = memo(
  function EditableProductTagsInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoProductTagsSegmentSchema.parse(step.segment),
      [step],
    );

    const handleCartOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        setStep({ ...step, segment: { ...segment, cartOperator: operator } });
      },
    );

    const handleOperatorSelected = useHandler(
      (operator: ProductTagsSegmentListMultipleItemMembershipOperator) => {
        setStep({ ...step, segment: { ...segment, operator } });
      },
    );

    const handleTagsSelected = useHandler((tags: string[]) => {
      const updatedSegment = { ...segment, tags };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Flex>
            <OperatorSelector<SegmentListMultipleItemMembershipOperator>
              availableOptions={Object.values(
                SegmentListMultipleItemMembershipOperator,
              )}
              onOperatorSelected={handleCartOperatorSelected}
              operatorToText={segmentListMultipleItemMembershipOperatorToText}
              selectedOperator={segment.cartOperator}
            />
          </Flex>
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          product in the cart has
        </Text>
        <Flex>
          <OperatorSelector<ProductTagsSegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              ProductTagsSegmentListMultipleItemMembershipOperatorEnum,
            )}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={
              productTagsSegmentListMultipleItemMembershipOperatorToText
            }
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          tag in the list:
        </Text>
        <ProductTagsSelector
          onTagsSelected={handleTagsSelected}
          selectedTags={segment.tags}
        />
      </>
    );
  },
);
